import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, Plus, Star } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const Plantillas = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    content: '',
    variables: [],
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Error al cargar plantillas');
    }
  };

  const handleCreateTemplate = async () => {
    try {
      await api.post('/templates', newTemplate);
      toast.success('Plantilla creada exitosamente');
      setShowNewTemplate(false);
      setNewTemplate({ name: '', content: '', variables: [] });
      fetchTemplates();
    } catch (error) {
      toast.error('Error al crear plantilla');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-10 h-10 text-primary" strokeWidth={1.5} />
                <h1 className="text-4xl font-bold text-slate-900" data-testid="plantillas-title">
                  Plantillas PDF
                </h1>
              </div>
              <p className="text-lg text-slate-600">
                Gestiona plantillas personalizables para propuestas
              </p>
            </div>
            <Dialog open={showNewTemplate} onOpenChange={setShowNewTemplate}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="new-template-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Plantilla
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Crear Nueva Plantilla</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Nombre de la Plantilla</Label>
                    <Input
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                      placeholder="Ej: Plantilla Propuesta Estándar"
                      data-testid="template-name-input"
                    />
                  </div>
                  <div>
                    <Label>Contenido de la Plantilla</Label>
                    <Textarea
                      value={newTemplate.content}
                      onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                      rows={12}
                      placeholder="Escribe el contenido de tu plantilla aquí...\n\nPuedes usar variables como: {{cliente_nombre}}, {{precio_total}}, {{descripcion}}..."
                      className="font-mono text-sm"
                      data-testid="template-content-input"
                    />
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 mb-2 font-medium">
                      💡 Variables disponibles:
                    </p>
                    <p className="text-xs text-blue-700 font-mono">
                      {'{{cliente_nombre}}, {{cliente_empresa}}, {{titulo}}, {{descripcion}}, {{precio_total}}, {{folio}}'}
                    </p>
                  </div>
                  <Button onClick={handleCreateTemplate} className="w-full" data-testid="create-template-btn">
                    Crear Plantilla
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Info Card */}
          <Card className="p-6 mb-8 border-primary/20 bg-primary/5">
            <div className="flex items-start gap-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Sobre las Plantillas PDF
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Las plantillas te permiten personalizar el formato y contenido de tus propuestas PDF.
                  Puedes crear múltiples plantillas para diferentes tipos de servicios y usar variables
                  dinámicas que se reemplazarán automáticamente con los datos de cada propuesta.
                </p>
              </div>
            </div>
          </Card>

          {/* Templates List */}
          <div className="space-y-4">
            {templates.length === 0 ? (
              <Card className="p-12 text-center border-slate-200">
                <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-lg text-slate-600 mb-2">No hay plantillas creadas aún</p>
                <p className="text-sm text-slate-500 mb-6">
                  Crea tu primera plantilla para personalizar tus propuestas
                </p>
                <Button
                  onClick={() => setShowNewTemplate(true)}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Primera Plantilla
                </Button>
              </Card>
            ) : (
              templates.map((template) => (
                <Card
                  key={template.id}
                  className="p-6 border-slate-200 hover:shadow-md transition-shadow"
                  data-testid={`template-${template.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <FileText className="w-6 h-6 text-primary" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-bold text-slate-900">{template.name}</h3>
                          {template.is_default && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-accent/20 text-accent rounded-full text-xs font-medium">
                              <Star className="w-3 h-3 fill-current" />
                              Por defecto
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mb-3">
                          Creada el {new Date(template.created_at).toLocaleDateString('es-CL')}
                        </p>
                        <div className="bg-slate-50 rounded-lg p-4 mb-3">
                          <p className="text-sm text-slate-700 font-mono whitespace-pre-wrap line-clamp-4">
                            {template.content}
                          </p>
                        </div>
                        {template.variables && template.variables.length > 0 && (
                          <div>
                            <p className="text-xs text-slate-600 mb-2 font-medium">Variables utilizadas:</p>
                            <div className="flex flex-wrap gap-2">
                              {template.variables.map((variable, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-mono"
                                >
                                  {variable}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Plantillas;
